import { Component, Input, OnDestroy, OnInit, ViewContainerRef } from '@angular/core';
import { ImageCardItem, ImageCardComponent } from '../../molecules';
import { BreakpointService } from '../../../../services/breakpoints';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Breakpoints } from '@angular/cdk/layout';
import { CommonModule } from '@angular/common';
import { ButtonComponent } from '../../atoms';

@Component({
  selector: 'app-image-grid',
  templateUrl: './image-grid.component.html',
  styles: [
    `
      :host .placeholder img {
        height: 200px;
        background-size: cover;
        background-position: center;
      }
    `,
  ],
  standalone: true,
  imports: [CommonModule, ImageCardComponent, ButtonComponent],
})
export class ImageGridComponent implements OnInit, OnDestroy {
  @Input() images: ImageCardItem[] = [];

  @Input() colsOnMobile: number = 1;
  @Input() colsOnDesktop: number = 3;
  @Input() initialItemsOnMobile: number = 1;
  @Input() initialItemsOnDesktop: number = 3;

  @Input() customRowClass: string;
  @Input() radius: number = 2;
  @Input() hasShadow: boolean = false;

  @Input() showToggler: boolean = true;
  @Input() togglerButtonLabels: string[] = ['Ver más', 'Ver menos'];

  @Input() scrollAnchorElement?: HTMLElement;
  @Input() scrollAnchorId?: string;

  @Input() showPlaceholder: boolean = false;

  private readonly destroy$ = new Subject<void>();
  private isMobile: boolean = true;

  visibleItems: number = this.initialItemsOnMobile;

  constructor(
    private readonly breakpointService: BreakpointService,
    private readonly viewContainerRef: ViewContainerRef
  ) {}

  ngOnInit(): void {
    this.observeScreenSize();
  }

  private observeScreenSize() {
    this.breakpointService
      .isMatchingBreakpoint(Breakpoints.Handset)
      .pipe(takeUntil(this.destroy$))
      .subscribe((matches) => {
        this.isMobile = matches;
        this.visibleItems = this.isMobile ? this.initialItemsOnMobile : this.initialItemsOnDesktop;
      });
  }

  get shouldShowToggler(): boolean {
    if (!this.showToggler) return false;

    const limit = this.isMobile ? this.initialItemsOnMobile : this.initialItemsOnDesktop;
    return this.images.length > limit;
  }

  toggleVisibility(): void {
    const isAllVisible = this.visibleItems >= this.images.length;

    // If all items are already visible, reset to the initial amount
    if (isAllVisible) {
      this.visibleItems = this.isMobile ? this.initialItemsOnMobile : this.initialItemsOnDesktop;
      if (this.scrollAnchorElement || this.scrollAnchorId) this.scrollToAnchor();
      return;
    }

    // On mobile with only 1 column, increase by the initial mobile batch
    if (this.isMobile && this.colsOnMobile === 1) {
      this.visibleItems = Math.min(
        this.visibleItems + this.initialItemsOnMobile,
        this.images.length
      );
      return;
    }

    // In other cases, increase by one full row (based on a number of columns)
    const cols = this.isMobile ? this.colsOnMobile : this.colsOnDesktop;
    this.visibleItems = Math.min(this.visibleItems + cols, this.images.length);
  }

  private scrollToAnchor() {
    let targetElement: HTMLElement | null;

    // Use scrollAnchorElement as the target if provided,
    // otherwise attempt to find the target element using the scrollAnchorId.
    if (this.scrollAnchorElement) targetElement = this.scrollAnchorElement;
    else {
      const parentElement = this.viewContainerRef.element.nativeElement.parentElement;
      targetElement = parentElement?.querySelector(`#${this.scrollAnchorId}`);
    }

    // If a valid target element is found, scroll to it with the appropriate offset
    if (targetElement) {
      const elementPosition = targetElement.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({ top: elementPosition, behavior: 'smooth' });
    }
  }

  createPlaceholders(count: number): any[] {
    return this.showPlaceholder ? Array(count) : [];
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
